import { addLines } from '@theme/dialed-cart';

/**
 * <dialed-size-calc>: sizing calculator (sections/dialed-size-calc.liquid). Lays out the system for a
 * width × length area on the 2 in grid, cab end at the top:
 * - 12 in grid fills the middle; the 2 in left over across the width becomes edge fill columns
 *   split between both sides, the 2 in left over along the length becomes edge fill rows at the cab
 *   end, and corner fill closes the gaps where they meet.
 * - The ramp edge (optional) takes the last 2 in at the tailgate: a 12 in ramp under each grid
 *   column and a 2 in ramp under each edge column.
 * Pieces use the truck layout format (P grid, S edge fill, T corner fill, R ramp) so they draw with
 * the same .dl-layout__piece styles.
 */
const SVG = 'http://www.w3.org/2000/svg';

/**
 * @param {number} width
 * @param {number} length
 * @param {boolean} ramp
 */
export function sizeLayout(width, length, ramp) {
  const w = Math.floor(width / 2) * 2;
  const l = Math.floor(length / 2) * 2;
  const field = ramp ? l - 2 : l;
  const cols = Math.floor(w / 12);
  const rows = Math.floor(field / 12);
  const sideCols = (w - cols * 12) / 2;
  const left = Math.floor(sideCols / 2);
  const topRows = (field - rows * 12) / 2;
  const x0 = left * 2;
  const y0 = topRows * 2;

  /** @type {Array<{type:string,x:number,y:number,w:number,h:number}>} */
  const pieces = [];
  const sideXs = [];
  for (let c = 0; c < left; c++) sideXs.push(c * 2);
  for (let c = 0; c < sideCols - left; c++) sideXs.push(x0 + cols * 12 + c * 2);

  for (let r = 0; r < topRows; r++) {
    for (let i = 0; i < cols; i++) pieces.push({ type: 'S', x: x0 + i * 12, y: r * 2, w: 12, h: 2 });
    for (const x of sideXs) pieces.push({ type: 'T', x, y: r * 2, w: 2, h: 2 });
  }
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) pieces.push({ type: 'P', x: x0 + i * 12, y: y0 + j * 12, w: 12, h: 12 });
    for (const x of sideXs) pieces.push({ type: 'S', x, y: y0 + j * 12, w: 2, h: 12 });
  }
  if (ramp) {
    for (let i = 0; i < cols; i++) pieces.push({ type: 'R', x: x0 + i * 12, y: field, w: 12, h: 2 });
    for (const x of sideXs) pieces.push({ type: 'R', x, y: field, w: 2, h: 2 });
  }

  const count = (/** @type {(p: typeof pieces[number]) => boolean} */ test) => pieces.filter(test).length;
  return {
    width: w,
    length: l,
    pieces,
    counts: {
      grid: count((p) => p.type === 'P'),
      edge: count((p) => p.type === 'S'),
      corner: count((p) => p.type === 'T'),
      ramp: count((p) => p.type === 'R' && p.w === 12),
      ramp_small: count((p) => p.type === 'R' && p.w === 2),
    },
  };
}

class DialedSizeCalc extends HTMLElement {
  /** @type {Record<string, {id:number,price:number,available:boolean} | null>} */
  products = {};
  /** @type {Array<{id:number,quantity:number}>} */
  lines = [];
  #abort = new AbortController();

  connectedCallback() {
    try {
      this.products = JSON.parse(this.querySelector('[data-calc-products]')?.textContent ?? '{}');
    } catch {
      return;
    }
    const { signal } = this.#abort;
    this.addEventListener('input', () => this.#update(), { signal });
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'number') this.#clamp(event.target);
      this.#update();
    }, { signal });
    this.#el('cta')?.addEventListener('click', () => this.#buy(), { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {string} name */
  #el(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-calc="${name}"]`));
  }

  /** @param {HTMLInputElement} input */
  #clamp(input) {
    const value = Number(input.value);
    if (!input.value || Number.isNaN(value)) return;
    input.value = String(Math.round(Math.min(Number(input.max), Math.max(Number(input.min), value))));
  }

  /** Reads one size input; shows its feet + inches, or the allowed range when it's out of it */
  #size(/** @type {string} */ name) {
    const input = /** @type {HTMLInputElement | null} */ (this.#el(name));
    const hint = this.#el(`${name}-hint`);
    if (!input) return null;
    const value = Number(input.value);
    const min = Number(input.min);
    const max = Number(input.max);
    const ok = Boolean(input.value) && value >= min && value <= max;
    input.toggleAttribute('aria-invalid', !ok);
    if (hint) {
      hint.toggleAttribute('data-error', !ok);
      hint.textContent = ok
        ? this.#t('Feet', { feet: String(Math.floor(value / 12)), inches: String(Math.floor(value % 12)) })
        : this.#t('Range', { min: String(min), max: String(max) });
    }
    return ok ? value : null;
  }

  #update() {
    const width = this.#size('width');
    const length = this.#size('length');
    const ramp = /** @type {HTMLInputElement | null} */ (this.#el('ramp'))?.checked ?? true;
    const map = this.#el('map');
    const cta = /** @type {HTMLButtonElement | null} */ (this.#el('cta'));
    const added = this.#el('added');
    if (added) added.hidden = true;

    if (width === null || length === null) {
      this.lines = [];
      if (cta) cta.disabled = true;
      const missing = this.#el('missing');
      if (missing) missing.hidden = true;
      return;
    }

    const layout = sizeLayout(width, length, ramp);
    if (map) map.replaceChildren(this.#draw(layout));
    const line = this.#el('line');
    if (line) line.textContent = this.#t('Size', { width: String(layout.width), length: String(layout.length) });

    // The blocker option on top of the layout
    const option = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-calc-option]:checked'));
    const counts = {
      ...layout.counts,
      t_pack: Number(option?.dataset.tPacks) || 0,
      adj_pack: Number(option?.dataset.adjPacks) || 0,
    };

    let base = 0;
    let total = 0;
    let ready = true;
    this.lines = [];
    for (const row of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-part]'))) {
      const key = /** @type {keyof typeof counts} */ (row.dataset.part);
      const qty = counts[key] ?? 0;
      const product = this.products[key];
      row.hidden = !qty;
      const qtyCell = row.querySelector('[data-part-qty]');
      const priceCell = row.querySelector('[data-part-price]');
      if (qtyCell) qtyCell.textContent = `× ${qty}`;
      if (!qty) continue;
      if (!product || !product.available) {
        ready = false;
        if (priceCell) priceCell.textContent = '—';
        continue;
      }
      const sum = product.price * qty;
      if (priceCell) priceCell.textContent = this.#money(sum);
      total += sum;
      if (key !== 't_pack' && key !== 'adj_pack') base += sum;
      this.lines.push({ id: product.id, quantity: qty });
    }

    // Each option card shows what it costs on top of the grid
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[data-calc-option]'))) {
      const extra =
        (Number(input.dataset.tPacks) || 0) * (this.products.t_pack?.price ?? 0) +
        (Number(input.dataset.adjPacks) || 0) * (this.products.adj_pack?.price ?? 0);
      const cell = input.closest('label')?.querySelector('[data-calc="option-price"]');
      if (cell) cell.textContent = extra ? `+${this.#money(extra)}` : this.#money(base);
    }

    const totalEl = this.#el('total');
    if (totalEl) totalEl.textContent = this.#money(total);
    const monthly = this.#el('monthly');
    if (monthly) {
      const min = Number(this.dataset.affirmMin);
      const months = Number(this.dataset.affirmMonths) || 12;
      monthly.hidden = min < 0 || total < min;
      monthly.textContent = this.#t('Monthly', { amount: this.#money(Math.round(total / months), { trim: false }) });
    }
    const label = this.#el('cta-label');
    if (label) label.textContent = this.#t('Cta').replaceAll('[price]', this.#money(total));
    if (cta) cta.disabled = !ready || !this.lines.length;
    const missing = this.#el('missing');
    if (missing) missing.hidden = ready;
    this.size = `${layout.width} × ${layout.length} in`;
  }

  /** @param {ReturnType<typeof sizeLayout>} layout */
  #draw(layout) {
    const svg = document.createElementNS(SVG, 'svg');
    svg.setAttribute('viewBox', `-1 -1 ${layout.width + 2} ${layout.length + 2}`);
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', `${this.dataset.mapLabel ?? ''} ${layout.width} × ${layout.length} in`.trim());
    const bed = document.createElementNS(SVG, 'rect');
    Object.entries({ x: 0, y: 0, width: layout.width, height: layout.length, class: 'dl-layout__bed' }).forEach(([k, v]) =>
      bed.setAttribute(k, String(v)),
    );
    svg.append(bed);
    for (const p of layout.pieces) {
      const r = document.createElementNS(SVG, 'rect');
      Object.entries({
        x: p.x + 0.2,
        y: p.y + 0.2,
        width: Math.max(p.w - 0.4, 0.4),
        height: Math.max(p.h - 0.4, 0.4),
        class: `dl-layout__piece dl-layout__piece--${p.type}`,
      }).forEach(([k, v]) => r.setAttribute(k, String(v)));
      svg.append(r);
    }
    return svg;
  }

  async #buy() {
    const cta = /** @type {HTMLButtonElement | null} */ (this.#el('cta'));
    if (!this.lines.length || !cta) return;
    const properties = { [this.dataset.tSizedTo || 'Sized to']: this.size ?? '' };
    cta.disabled = true;
    cta.setAttribute('aria-busy', 'true');
    const { ok } = await addLines(this, this.lines.map((line) => ({ ...line, properties })), { sourceName: 'dialed-size-calc' });
    cta.disabled = false;
    cta.removeAttribute('aria-busy');

    const message = ok ? this.#t('Added') : this.#t('Error');
    const live = this.#el('live');
    if (live) live.textContent = message;
    const added = this.#el('added');
    const text = this.#el('added-text');
    if (added && text) {
      text.textContent = message;
      added.hidden = false;
    }
  }

  /**
   * @param {string} key
   * @param {Record<string, string>} [values]
   */
  #t(key, values = {}) {
    let text = this.dataset[`t${key}`] ?? '';
    for (const [k, v] of Object.entries(values)) text = text.replaceAll(`[${k}]`, v);
    return text;
  }

  /**
   * @param {number} cents
   * @param {{ trim?: boolean }} [options]
   */
  #money(cents, { trim = true } = {}) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const commas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = trim && dec === '00' ? commas : `${commas}.${dec}`;
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, commas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/<[^>]+>/g, '');
  }
}

if (!customElements.get('dialed-size-calc')) customElements.define('dialed-size-calc', DialedSizeCalc);
